import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { DarkModeProvider } from "./context/DarkModeContext";
import { Toaster } from "sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";

const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Settings = lazy(() => import("./pages/Settings"));
const Unauthorized = lazy(() => import("./pages/Unauthorized"));
const UserList = lazy(() => import("./pages/admin/UserList"));
const AudioFiles = lazy(() => import("./pages/AudioFiles"));
const AudioFileSections = lazy(() => import("./pages/AudioFileSections"));
const AudioFileSectionView = lazy(() => import("./pages/AudioFileSectionView"));
const AudioFileNew = lazy(() => import("./pages/AudioFileNew"));
const AudioFileEdit = lazy(() => import("./pages/AudioFileEdit"));
const AudioFileView = lazy(() => import("./pages/AudioFileView"));
const AudioEmotions = lazy(() => import("./pages/AudioEmotions"));
const Reports = lazy(() => import("./pages/Reports"));

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-orange-50/70 dark:bg-gray-950/70">
      <div className="flex items-center gap-3 rounded-2xl border border-orange-200 bg-white/90 px-5 py-4 text-sm text-gray-700 shadow-sm dark:border-gray-800 dark:bg-gray-900/90 dark:text-gray-200">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-orange-400 border-t-transparent" />
        Loading page...
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <DarkModeProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors closeButton />
          <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* Protected routes for all authenticated users */}
              <Route element={<ProtectedRoute />}>
                <Route element={<Layout />}>
                  <Route path="/home" element={<Home />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/audio-file-sections" element={<AudioFileSections />} />
                  <Route path="/audio-file-sections/:id" element={<AudioFileSectionView />} />
                  <Route path="/audio-emotions" element={<AudioEmotions />} />
                  <Route path="/audio-files" element={<AudioFiles />} />
                  <Route path="/audio-files/new" element={<AudioFileNew />} />
                  <Route path="/audio-files/edit/:id" element={<AudioFileEdit />} />
                  <Route path="/audio-files/view/:id" element={<AudioFileView />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />
                </Route>
              </Route>

              {/* Admin only routes */}
              <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
                <Route element={<Layout />}>
                  <Route path="/admin/users" element={<UserList />} />
                  <Route path="/admin/reports" element={<Reports />} />
                </Route>
              </Route>

              {/* Redirects and fallback */}
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route
                path="*"
                element={
                  <div className="min-h-screen flex items-center justify-center">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      404 - Page Not Found
                    </h1>
                  </div>
                }
              />
            </Routes>
          </Suspense>
        </AuthProvider>
      </DarkModeProvider>
    </Router>
  );
}

export default App;
